import * as React from "react";
import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigDelete, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  },
  container: {
    width: "100%",
    maxWidth: "800px",
    boxSizing: "border-box",
    padding: "0 1em",
    minWidth: 0,
    overflow: "hidden",
  },
  pageTitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "2rem",
  },
  card: {
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    marginBottom: "2em",
  },
  form: {
    padding: "2em",
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  submitButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "1em",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  resultCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    color: "white",
  },
  resultTitle: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: "1em",
    fontSize: "1.2rem",
    fontWeight: 600,
  },
  resultItem: {
    marginBottom: "1em",
    padding: "0.75em",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "8px",
  },
  resultLabel: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.9rem",
    marginBottom: "0.25em",
  },
  resultValue: {
    color: "white",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  infoBox: {
    marginBottom: "2em",
    padding: "1.5em",
    backgroundColor: "#e3f2fd",
    borderRadius: "8px",
    borderLeft: "4px solid #1565c0",
  },
  infoText: {
    color: "#1565c0",
    fontSize: "0.95rem",
  },
  tabsContainer: {
    marginBottom: "2em",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
    minWidth: 120,
  },
  refundRow: {
    marginBottom: "1em",
    padding: "1em",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
  },
  refundRowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.5em",
  },
  refundRowContent: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "16px",
  },
  fieldPair: {
    minWidth: "140px",
  },
  fieldLabel: {
    fontSize: "0.75rem",
    color: "#666",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  fieldValue: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#333",
  },
  statusBadge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: 600,
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
  },
  detailsButton: {
    textTransform: "none" as const,
    fontSize: "0.75rem",
  },
  jsonBlock: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    padding: "1em",
    borderRadius: "8px",
    fontSize: "0.8rem",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    overflowX: "auto" as const,
    maxHeight: "400px",
  },
  jsonSectionTitle: {
    fontWeight: 700,
    marginTop: "1em",
    marginBottom: "0.5em",
  },
}));

interface NatwestRefundResult {
  success: boolean;
  refund_type: string;
  user_id: number;
  user_name: string;
  amount: number;
  payment_reference: string;
  sort_code: string;
  account_number: string;
  natwest_response?: any;
  raw_request?: any;
  raw_response?: any;
}

interface RefundLookupSource {
  sort_code: string;
  account_number: string;
  account_holder_name: string;
}

interface RefundLookupResult {
  success: boolean;
  refund_type: string;
  eligible: boolean;
  ineligible_reason?: string;
  transaction: {
    id: number;
    amount: number;
    status: string;
    is_refunded: boolean;
    brand_name?: string | null;
  };
  source_details_found: boolean;
  source: RefundLookupSource | null;
  refund_provider?: string;
  requires_bank_details?: boolean;
}

interface RefundSendResult {
  success: boolean;
  flag: string;
  provider?: string;
  transaction_id: string;
  gift_card_user_payment_id?: number;
  anywhere_card_id?: number;
  user_id: number;
  user_name: string;
  amount: number;
  sort_code: string;
  account_number: string;
  payment_reference: string;
  message_id: string;
  instruction_id: string;
  received_at: string;
}

interface RefundRecord {
  refund_id: number;
  user_id: number;
  user_name: string;
  email?: string;
  amount: number;
  gift_card_user_payment_id?: number;
  provider?: string;
  refund_type: string;
  transfer_status?: string | null;
  provider_status?: string | null;
  provider_transfer_id?: string | null;
  error_message?: string | null;
  created_at?: string;
  createdAt?: string;
  payment_reference?: string;
  source_details?: any;
  response_details?: any;
}

interface LinkTrackingResponse {
  success: boolean;
  email: string;
  user_id: number;
  user_name: string;
  total_refunds: number;
  gift_card_refunds: RefundRecord[];
  manual_refunds: RefundRecord[];
  anywhere_card_refunds: RefundRecord[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`natwest-tabpanel-${index}`}
      aria-labelledby={`natwest-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const PROVIDER_LABELS: Record<string, string> = {
  natwest: "NatWest FPS",
  payit: "Payit",
  revolut: "Revolut",
};

const providerLabel = (provider?: string | null): string =>
  (provider && PROVIDER_LABELS[provider]) || provider || "Unknown";

const TRANSFER_STATUS_COLOURS: Record<string, { bg: string; fg: string }> = {
  completed: { bg: "#e8f5e9", fg: "#2e7d32" },
  failed: { bg: "#ffebee", fg: "#c62828" },
};

const formatDate = (d: string | null | undefined): string => {
  if (!d) return "N/A";
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
  } catch {
    return "N/A";
  }
};

const NatwestTransferDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [activeTab, setActiveTab] = useState(0);

  // ── Manual Refund ──
  const [mrEmail, setMrEmail] = useState("");
  const [mrName, setMrName] = useState("");
  const [mrSortCode, setMrSortCode] = useState("");
  const [mrAccountNumber, setMrAccountNumber] = useState("");
  const [mrAmount, setMrAmount] = useState("");
  const [mrPaymentReference, setMrPaymentReference] = useState("");
  const [mrLoading, setMrLoading] = useState(false);
  const [mrResult, setMrResult] = useState<NatwestRefundResult | null>(null);
  const [mrConfirmOpen, setMrConfirmOpen] = useState(false);

  // ── Live Tracking ──
  const [ltEmail, setLtEmail] = useState("");
  const [ltLoading, setLtLoading] = useState(false);
  const [ltLinkResult, setLtLinkResult] = useState<LinkTrackingResponse | null>(null);

  // ── Gift Card / Anywhere Refund ──
  const [rType, setRType] = useState<"giftcard" | "anywhere">("giftcard");
  const [rEmail, setREmail] = useState("");
  const [rTransactionId, setRTransactionId] = useState("");
  const [rLookupLoading, setRLookupLoading] = useState(false);
  const [rLookup, setRLookup] = useState<RefundLookupResult | null>(null);
  const [rName, setRName] = useState("");
  const [rSortCode, setRSortCode] = useState("");
  const [rAccountNumber, setRAccountNumber] = useState("");
  const [rSendLoading, setRSendLoading] = useState(false);
  const [rSendResult, setRSendResult] = useState<RefundSendResult | null>(null);
  const [rConfirmOpen, setRConfirmOpen] = useState(false);

  // ── Raw details dialog (shared) ──
  const [detailsDialog, setDetailsDialog] = useState<{
    title: string;
    source: any;
    response: any;
  } | null>(null);

  const [archiveDialog, setArchiveDialog] = useState<RefundRecord | null>(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveLoading, setArchiveLoading] = useState(false);

  // ═══════════════════════════════════════════════════
  // Manual Refund handlers
  // ═══════════════════════════════════════════════════

  const validateManualRefund = () => {
    if (!mrEmail.trim()) { notify("Email is required", "error"); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mrEmail.trim())) { notify("Invalid email", "error"); return false; }
    if (!mrName.trim()) { notify("Name is required", "error"); return false; }
    const cleanSort = mrSortCode.replace(/[-\s]/g, "");
    if (!/^\d{6}$/.test(cleanSort)) { notify("Sort code must be 6 digits", "error"); return false; }
    const cleanAcct = mrAccountNumber.replace(/\s/g, "");
    if (!/^\d{8}$/.test(cleanAcct)) { notify("Account number must be 8 digits", "error"); return false; }
    if (!mrAmount || isNaN(Number(mrAmount)) || Number(mrAmount) <= 0) {
      notify("Amount must be a positive number", "error"); return false;
    }
    return true;
  };

  const handleManualRefund = async () => {
    setMrConfirmOpen(false);
    try {
      setMrLoading(true);
      const res = await axios.post<{ data: NatwestRefundResult }>(
        ENDPOINTS.NATWEST_TRANSFER,
        {
          flag: "manual_refund",
          email: mrEmail.trim(),
          name: mrName.trim(),
          sort_code: mrSortCode.replace(/[-\s]/g, ""),
          account_number: mrAccountNumber.replace(/\s/g, ""),
          amount: Number(mrAmount),
          ...(mrPaymentReference.trim() ? { payment_reference: mrPaymentReference.trim() } : {}),
        },
        { headers: getConfigPost() }
      );
      const data = res.data.data || (res.data as unknown as NatwestRefundResult);
      setMrResult(data);
      notify("Manual refund sent via NatWest FPS!", "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "Manual refund failed", "error");
    } finally {
      setMrLoading(false);
    }
  };

  const handleResetMr = () => {
    setMrEmail(""); setMrName(""); setMrSortCode("");
    setMrAccountNumber(""); setMrAmount(""); setMrPaymentReference(""); setMrResult(null);
  };

  // ═══════════════════════════════════════════════════
  // Gift Card / Anywhere Refund handlers
  // ═══════════════════════════════════════════════════

  const handleRefundLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rEmail.trim()) { notify("Email is required", "error"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rEmail.trim())) { notify("Invalid email", "error"); return; }
    if (!rTransactionId.trim()) { notify("Transaction ID is required", "error"); return; }
    try {
      setRLookupLoading(true);
      setRLookup(null);
      setRSendResult(null);
      setRName(""); setRSortCode(""); setRAccountNumber("");
      const url = `${ENDPOINTS.NATWEST_TRANSFER}?flag=refund_lookup&email=${encodeURIComponent(rEmail.trim())}&transaction_id=${encodeURIComponent(rTransactionId.trim())}&refund_type=${rType}`;
      const res = await axios.get<{ data: RefundLookupResult }>(url, { headers: getConfigPost() });
      const data = res.data.data || (res.data as unknown as RefundLookupResult);
      setRLookup(data);
      if (data.eligible && data.source_details_found && data.source) {
        setRSortCode(data.source.sort_code || "");
        setRAccountNumber(data.source.account_number || "");
        setRName(data.source.account_holder_name || "");
      }
      notify(data.eligible ? "Transaction eligible for refund" : `Not eligible: ${data.ineligible_reason}`, data.eligible ? "info" : "warning");
    } catch (err: any) {
      notify(err.response?.data?.message || "Lookup failed", "error");
    } finally {
      setRLookupLoading(false);
    }
  };

  const needsBankDetails = rLookup?.requires_bank_details !== false;

  const validateRefundSend = () => {
    if (!needsBankDetails) return true;
    if (!rName.trim()) { notify("Recipient name is required", "error"); return false; }
    const cleanSort = rSortCode.replace(/[-\s]/g, "");
    if (!/^\d{6}$/.test(cleanSort)) { notify("Sort code must be 6 digits", "error"); return false; }
    const cleanAcct = rAccountNumber.replace(/\s/g, "");
    if (!/^\d{8}$/.test(cleanAcct)) { notify("Account number must be 8 digits", "error"); return false; }
    return true;
  };

  const handleRefundSend = async () => {
    setRConfirmOpen(false);
    const flag = rType === "giftcard" ? "giftcard_refund" : "anywhere_refund";
    try {
      setRSendLoading(true);
      const res = await axios.post<{ data: RefundSendResult }>(
        ENDPOINTS.NATWEST_TRANSFER,
        {
          flag,
          email: rEmail.trim(),
          transaction_id: rTransactionId.trim(),
          name: rName.trim(),
          sort_code: rSortCode.replace(/[-\s]/g, ""),
          account_number: rAccountNumber.replace(/\s/g, ""),
        },
        { headers: getConfigPost() }
      );
      const data = res.data.data || (res.data as unknown as RefundSendResult);
      setRSendResult(data);
      notify(`Refund sent via ${providerLabel(data.provider || rLookup?.refund_provider)}!`, "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "Refund failed", "error");
    } finally {
      setRSendLoading(false);
    }
  };

  const handleResetRefund = () => {
    setREmail(""); setRTransactionId(""); setRLookup(null);
    setRName(""); setRSortCode(""); setRAccountNumber(""); setRSendResult(null);
  };

  // ═══════════════════════════════════════════════════
  // Live Tracking handlers
  // ═══════════════════════════════════════════════════

  const handleLinkTracking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ltEmail.trim()) { notify("Email is required", "error"); return; }
    try {
      setLtLoading(true);
      setLtLinkResult(null);
      const url = `${ENDPOINTS.NATWEST_TRANSFER}?flag=refund_history&email=${encodeURIComponent(ltEmail.trim())}`;
      const res = await axios.get<{ data: LinkTrackingResponse }>(url, { headers: getConfigPost() });
      const data = res.data.data || (res.data as unknown as LinkTrackingResponse);
      setLtLinkResult(data);
      notify(`Found ${data.total_refunds} refund(s)`, "info");
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to fetch tracking data", "error");
    } finally {
      setLtLoading(false);
    }
  };

  const handleArchiveRefund = async () => {
    if (!archiveDialog) return;
    if (!archiveReason.trim()) { notify("Reason is required", "error"); return; }
    try {
      setArchiveLoading(true);
      const url = `${ENDPOINTS.GIFT_CARD_REFUND_ARCHIVE}/${archiveDialog.refund_id}?cancel_reason=${encodeURIComponent(archiveReason.trim())}`;
      await axios.delete(url, { headers: getConfigDelete() });
      notify(`Refund #${archiveDialog.refund_id} archived`, "info");
      setArchiveDialog(null);
      setArchiveReason("");
      if (ltLinkResult) {
        const refreshed = ltLinkResult.gift_card_refunds.filter(
          (r) => r.refund_id !== archiveDialog.refund_id
        );
        setLtLinkResult({
          ...ltLinkResult,
          gift_card_refunds: refreshed,
          total_refunds: ltLinkResult.total_refunds - 1,
        });
      }
    } catch (err: any) {
      notify(err.response?.data?.message || "Failed to archive refund", "error");
    } finally {
      setArchiveLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════
  // Render helpers
  // ═══════════════════════════════════════════════════

  const ResultField = ({ label, value }: { label: string; value: any }) => (
    <div className={classes.resultItem}>
      <Typography className={classes.resultLabel}>{label}</Typography>
      <Typography className={classes.resultValue}>{value ?? "N/A"}</Typography>
    </div>
  );

  const RefundList = ({
    title,
    badgeLabel,
    refunds,
    archivable = false,
  }: {
    title: string;
    badgeLabel: string;
    refunds: RefundRecord[];
    archivable?: boolean;
  }) => (
    <Box style={{ marginBottom: "2em" }}>
      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700 }}>
        {title} ({refunds.length})
      </Typography>
      {refunds.length === 0 && (
        <Typography color="textSecondary" variant="body2">None found.</Typography>
      )}
      {refunds.map((r, i) => (
        <div key={i} className={classes.refundRow}>
          <div className={classes.refundRowHeader}>
            <Typography variant="body2"><strong>Refund #{r.refund_id}</strong></Typography>
            <span className={classes.statusBadge}>{badgeLabel} / {providerLabel(r.provider)}</span>
          </div>
          <div className={classes.refundRowContent}>
            <div className={classes.fieldPair}>
              <div className={classes.fieldLabel}>Amount</div>
              <div className={classes.fieldValue}>£{r.amount}</div>
            </div>
            {r.transfer_status && (
              <div className={classes.fieldPair}>
                <div className={classes.fieldLabel}>Status</div>
                <div className={classes.fieldValue}>
                  <Chip
                    size="small"
                    label={r.transfer_status}
                    style={{
                      backgroundColor: TRANSFER_STATUS_COLOURS[r.transfer_status]?.bg || "#fff8e1",
                      color: TRANSFER_STATUS_COLOURS[r.transfer_status]?.fg || "#8d6e00",
                      fontWeight: 700,
                    }}
                  />
                </div>
              </div>
            )}
            {r.provider_status && (
              <div className={classes.fieldPair}>
                <div className={classes.fieldLabel}>{providerLabel(r.provider)} Status</div>
                <div className={classes.fieldValue}>{r.provider_status}</div>
              </div>
            )}
            {r.provider_transfer_id && (
              <div className={classes.fieldPair}>
                <div className={classes.fieldLabel}>Provider Ref</div>
                <div className={classes.fieldValue}>{r.provider_transfer_id}</div>
              </div>
            )}
            {r.error_message && (
              <div className={classes.fieldPair}>
                <div className={classes.fieldLabel}>Error</div>
                <div className={classes.fieldValue} style={{ color: "#c62828" }}>{r.error_message}</div>
              </div>
            )}
            <div className={classes.fieldPair}>
              <div className={classes.fieldLabel}>Payment Ref</div>
              <div className={classes.fieldValue}>{r.payment_reference || r.source_details?.payment_reference || "—"}</div>
            </div>
            <div className={classes.fieldPair}>
              <div className={classes.fieldLabel}>Created</div>
              <div className={classes.fieldValue}>{formatDate(r.created_at || r.createdAt)}</div>
            </div>
          </div>
          {(r.source_details || r.response_details) && (
            <Button
              size="small"
              variant="outlined"
              className={classes.detailsButton}
              style={{ marginTop: "0.75em" }}
              onClick={() =>
                setDetailsDialog({
                  title: `Refund #${r.refund_id} — Raw Details`,
                  source: r.source_details,
                  response: r.response_details,
                })
              }
            >
              View Raw Request / Response
            </Button>
          )}
          {archivable && (
            <Button
              size="small"
              variant="outlined"
              style={{ marginTop: "0.75em", marginLeft: "0.5em", color: "#c62828", borderColor: "#c62828" }}
              onClick={() => { setArchiveReason(""); setArchiveDialog(r); }}
            >
              Archive Refund
            </Button>
          )}
        </div>
      ))}
    </Box>
  );

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <Typography variant="h4" className={classes.pageTitle}>
          Refunds
        </Typography>

        {/* Tabs */}
        <Paper className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={(_: any, v: number) => setActiveTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab className={classes.tab} label="Manual Refund" />
            <Tab className={classes.tab} label="Gift Card Refund" />
            <Tab className={classes.tab} label="Live Tracking" />
          </Tabs>
        </Paper>

        {/* ═══ TAB 0: Manual Refund ═══ */}
        <TabPanel value={activeTab} index={0}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Manual NatWest FPS Refund</Typography>
              <div className={classes.infoBox}>
                <Typography className={classes.infoText}>
                  Send a direct bank transfer via NatWest FPS. Provide the recipient's bank details and amount manually. This does not require a gift card transaction.
                </Typography>
              </div>

              <TextField
                label="Email"
                value={mrEmail}
                onChange={(e) => setMrEmail(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="user@example.com"
                disabled={mrLoading}
              />
              <TextField
                label="Recipient Name"
                value={mrName}
                onChange={(e) => setMrName(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="John Smith"
                disabled={mrLoading}
              />
              <TextField
                label="Sort Code"
                value={mrSortCode}
                onChange={(e) => setMrSortCode(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="20-00-00"
                disabled={mrLoading}
              />
              <TextField
                label="Account Number"
                value={mrAccountNumber}
                onChange={(e) => setMrAccountNumber(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="12345678"
                disabled={mrLoading}
              />
              <TextField
                label="Amount (£)"
                value={mrAmount}
                onChange={(e) => setMrAmount(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                type="number"
                inputProps={{ min: "0.01", step: "0.01" }}
                placeholder="10.00"
                disabled={mrLoading}
              />
              <TextField
                label="Payment Reference (optional)"
                value={mrPaymentReference}
                onChange={(e) => setMrPaymentReference(e.target.value)}
                fullWidth
                variant="outlined"
                className={classes.formField}
                placeholder="Leave empty for auto-generated"
                disabled={mrLoading}
              />

              <Button
                variant="contained"
                color="primary"
                className={classes.submitButton}
                disabled={mrLoading}
                onClick={() => { if (validateManualRefund()) setMrConfirmOpen(true); }}
                startIcon={mrLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {mrLoading ? "Sending..." : "Send Manual Refund"}
              </Button>
              {mrResult && (
                <Button
                  variant="outlined"
                  onClick={handleResetMr}
                  style={{ marginTop: "1em", marginLeft: "1em", textTransform: "none" }}
                >
                  Reset
                </Button>
              )}

              {mrResult && (
                <Paper className={classes.resultCard}>
                  <Typography className={classes.resultTitle}>✓ Manual Refund Sent</Typography>
                  <ResultField label="Payment Reference" value={mrResult.payment_reference} />
                  <ResultField label="Amount" value={`£${mrResult.amount}`} />
                  <ResultField label="Sort Code" value={mrResult.sort_code} />
                  <ResultField label="Account Number" value={mrResult.account_number} />
                  {(mrResult.raw_request || mrResult.raw_response) && (
                    <Button
                      variant="outlined"
                      style={{
                        marginTop: "0.5em",
                        textTransform: "none",
                        color: "white",
                        borderColor: "rgba(255,255,255,0.5)",
                      }}
                      onClick={() =>
                        setDetailsDialog({
                          title: "Manual Refund — Raw Request / Response",
                          source: mrResult.raw_request,
                          response: mrResult.raw_response,
                        })
                      }
                    >
                      View Raw Request / Response
                    </Button>
                  )}
                </Paper>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* ═══ TAB 1: Gift Card / Anywhere Refund ═══ */}
        <TabPanel value={activeTab} index={1}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Card Refund</Typography>

              {/* Step 1: Refund type + lookup */}
              <FormControl component="fieldset" style={{ marginBottom: "1.5em" }}>
                <FormLabel component="legend" style={{ marginBottom: "0.5em", fontWeight: 600 }}>Refund type</FormLabel>
                <RadioGroup
                  row
                  value={rType}
                  onChange={(e) => { setRType(e.target.value as "giftcard" | "anywhere"); setRLookup(null); setRSendResult(null); }}
                >
                  <FormControlLabel value="giftcard" control={<Radio color="primary" />} label="Gift Card" />
                  <FormControlLabel value="anywhere" control={<Radio color="primary" />} label="Anywhere Card" />
                </RadioGroup>
              </FormControl>

              <form onSubmit={handleRefundLookup}>
                <TextField
                  label="Email"
                  value={rEmail}
                  onChange={(e) => setREmail(e.target.value)}
                  fullWidth variant="outlined" className={classes.formField}
                  placeholder="user@example.com" disabled={rLookupLoading || rSendLoading}
                />
                <TextField
                  label={rType === "giftcard" ? "Transaction ID (simp_id)" : "Payment ID"}
                  value={rTransactionId}
                  onChange={(e) => setRTransactionId(e.target.value)}
                  fullWidth variant="outlined" className={classes.formField}
                  placeholder={rType === "giftcard" ? "e.g. sp_abc123..." : "e.g. sp_abc123..."}
                  disabled={rLookupLoading || rSendLoading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  disabled={rLookupLoading || rSendLoading || !rEmail.trim() || !rTransactionId.trim()}
                  startIcon={rLookupLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {rLookupLoading ? "Looking up..." : "Look Up"}
                </Button>
              </form>

              {/* Step 2: Lookup result */}
              {rLookup && !rSendResult && (
                <Box style={{ marginTop: "1.5em" }}>
                  {/* Transaction info */}
                  <Box style={{ padding: "1em", backgroundColor: "#f5f5f5", borderRadius: "8px", marginBottom: "1em" }}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 700 }}>
                      Transaction details
                      {" "}
                      <Chip
                        size="small"
                        label={rLookup.eligible ? "Eligible" : "Not eligible"}
                        style={{
                          backgroundColor: rLookup.eligible ? "#e8f5e9" : "#ffebee",
                          color: rLookup.eligible ? "#2e7d32" : "#c62828",
                          fontWeight: 700,
                          marginLeft: "0.5em",
                        }}
                      />
                    </Typography>
                    <div className={classes.refundRowContent}>
                      <div className={classes.fieldPair}>
                        <div className={classes.fieldLabel}>Amount</div>
                        <div className={classes.fieldValue}>£{rLookup.transaction.amount}</div>
                      </div>
                      <div className={classes.fieldPair}>
                        <div className={classes.fieldLabel}>Status</div>
                        <div className={classes.fieldValue}>{rLookup.transaction.status}</div>
                      </div>
                      {rLookup.transaction.brand_name && (
                        <div className={classes.fieldPair}>
                          <div className={classes.fieldLabel}>Brand</div>
                          <div className={classes.fieldValue}>{rLookup.transaction.brand_name}</div>
                        </div>
                      )}
                      <div className={classes.fieldPair}>
                        <div className={classes.fieldLabel}>Refund via</div>
                        <div className={classes.fieldValue}>{providerLabel(rLookup.refund_provider || "natwest")}</div>
                      </div>
                    </div>
                    {!rLookup.eligible && (
                      <Typography style={{ marginTop: "0.75em", color: "#c62828", fontWeight: 600 }}>
                        {rLookup.ineligible_reason}
                      </Typography>
                    )}
                  </Box>

                  {/* Source details + send form (only if eligible) */}
                  {rLookup.eligible && !needsBankDetails && (
                    <>
                      <div className={classes.infoBox} style={{ marginBottom: "1.5em" }}>
                        <Typography style={{ fontSize: "0.95rem" }}>
                          Refunded through {providerLabel(rLookup.refund_provider)} to the account that paid — no bank details needed.
                        </Typography>
                      </div>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.submitButton}
                        disabled={rSendLoading}
                        onClick={() => setRConfirmOpen(true)}
                        startIcon={rSendLoading ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        {rSendLoading ? "Sending..." : "Send Refund"}
                      </Button>
                    </>
                  )}

                  {rLookup.eligible && needsBankDetails && (
                    <>
                      {rLookup.source_details_found ? (
                        <div className={classes.infoBox} style={{ backgroundColor: "#e8f5e9", borderColor: "#2e7d32", marginBottom: "1.5em" }}>
                          <Typography style={{ color: "#2e7d32", fontSize: "0.95rem" }}>
                            ✓ Source details found on TrueLayer — pre-filled below. Verify before confirming.
                          </Typography>
                        </div>
                      ) : (
                        <div className={classes.infoBox} style={{ backgroundColor: "#fff3e0", borderColor: "#e65100", marginBottom: "1.5em" }}>
                          <Typography style={{ color: "#e65100", fontSize: "0.95rem" }}>
                            ⚠ Source details not found on TrueLayer. Please enter sort code, account number and recipient name manually.
                          </Typography>
                        </div>
                      )}

                      <TextField
                        label="Recipient Name"
                        value={rName}
                        onChange={(e) => setRName(e.target.value)}
                        fullWidth variant="outlined" className={classes.formField}
                        placeholder="John Smith" disabled={rSendLoading}
                      />
                      <TextField
                        label="Sort Code"
                        value={rSortCode}
                        onChange={(e) => setRSortCode(e.target.value)}
                        fullWidth variant="outlined" className={classes.formField}
                        placeholder="20-00-00" disabled={rSendLoading}
                      />
                      <TextField
                        label="Account Number"
                        value={rAccountNumber}
                        onChange={(e) => setRAccountNumber(e.target.value)}
                        fullWidth variant="outlined" className={classes.formField}
                        placeholder="12345678" disabled={rSendLoading}
                      />

                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.submitButton}
                        disabled={rSendLoading}
                        onClick={() => { if (validateRefundSend()) setRConfirmOpen(true); }}
                        startIcon={rSendLoading ? <CircularProgress size={20} color="inherit" /> : null}
                      >
                        {rSendLoading ? "Sending..." : "Send Refund"}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outlined"
                    onClick={handleResetRefund}
                    style={{ marginTop: "1em", marginLeft: "1em", textTransform: "none" }}
                  >
                    Reset
                  </Button>
                </Box>
              )}

              {/* Step 3: Success result */}
              {rSendResult && (
                <Box style={{ marginTop: "1.5em" }}>
                  <Paper className={classes.resultCard}>
                    <Typography className={classes.resultTitle}>✓ Refund Sent</Typography>
                    <ResultField label="Transaction ID" value={rSendResult.transaction_id} />
                    {rSendResult.gift_card_user_payment_id && <ResultField label="Gift Card Payment ID" value={rSendResult.gift_card_user_payment_id} />}
                    {rSendResult.anywhere_card_id && <ResultField label="Anywhere Card ID" value={rSendResult.anywhere_card_id} />}
                    <ResultField label="User" value={`${rSendResult.user_name} (ID: ${rSendResult.user_id})`} />
                    <ResultField label="Amount Refunded" value={`£${rSendResult.amount}`} />
                    <ResultField label="Refund via" value={providerLabel(rSendResult.provider || rLookup?.refund_provider || "natwest")} />
                    {rSendResult.sort_code && (
                      <>
                        <ResultField label="Sort Code" value={rSendResult.sort_code} />
                        <ResultField label="Account Number" value={rSendResult.account_number} />
                        <ResultField label="Payment Reference" value={rSendResult.payment_reference} />
                        <ResultField label="Message ID" value={rSendResult.message_id} />
                        <ResultField label="Received At" value={formatDate(rSendResult.received_at)} />
                      </>
                    )}
                  </Paper>
                  <Button
                    variant="outlined"
                    onClick={handleResetRefund}
                    style={{ marginTop: "1em", textTransform: "none" }}
                  >
                    New Refund
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* ═══ TAB 2: Live Tracking ═══ */}
        <TabPanel value={activeTab} index={2}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Refund Tracking (by Email)</Typography>
              <form onSubmit={handleLinkTracking} style={{ marginBottom: "2em" }}>
                <TextField
                  label="User Email"
                  value={ltEmail}
                  onChange={(e) => setLtEmail(e.target.value)}
                  fullWidth
                  variant="outlined"
                  className={classes.formField}
                  placeholder="user@example.com"
                  disabled={ltLoading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  disabled={ltLoading || !ltEmail.trim()}
                  startIcon={ltLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {ltLoading ? "Loading..." : "Search Refunds"}
                </Button>
              </form>

              {ltLinkResult && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>{ltLinkResult.user_name}</strong> ({ltLinkResult.email}) — {ltLinkResult.total_refunds} refund(s)
                  </Typography>

                  <RefundList
                    title="Gift Card Refunds"
                    badgeLabel="gift_card"
                    refunds={ltLinkResult.gift_card_refunds}
                    archivable
                  />

                  <RefundList
                    title="Manual Refunds"
                    badgeLabel="manual"
                    refunds={ltLinkResult.manual_refunds}
                  />

                  <RefundList
                    title="Anywhere Card Refunds"
                    badgeLabel="anywhere_card"
                    refunds={ltLinkResult.anywhere_card_refunds || []}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

        </TabPanel>
      </div>

      {/* ═══ Confirm: Manual Refund ═══ */}
      <Dialog open={mrConfirmOpen} onClose={() => setMrConfirmOpen(false)}>
        <DialogTitle>Confirm Manual NatWest Refund</DialogTitle>
        <DialogContent>
          <Typography>You are about to send:</Typography>
          <Typography style={{ marginTop: "0.5em" }}>
            <strong>£{mrAmount}</strong> to <strong>{mrName}</strong> ({mrEmail})
          </Typography>
          <Typography>
            Sort Code: <strong>{mrSortCode}</strong> — Account: <strong>{mrAccountNumber}</strong>
          </Typography>
          {mrPaymentReference.trim() && (
            <Typography>
              Payment Reference: <strong>{mrPaymentReference.trim()}</strong>
            </Typography>
          )}
          <Typography style={{ marginTop: "1em", color: "#c62828" }}>
            This will initiate a real bank transfer. Please confirm.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMrConfirmOpen(false)} color="default">Cancel</Button>
          <Button onClick={handleManualRefund} color="primary" variant="contained">Confirm & Send</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Confirm: Card Refund ═══ */}
      <Dialog open={rConfirmOpen} onClose={() => setRConfirmOpen(false)}>
        <DialogTitle>
          Confirm {rType === "giftcard" ? "Gift Card" : "Anywhere Card"} {providerLabel(rLookup?.refund_provider || "natwest")} Refund
        </DialogTitle>
        <DialogContent>
          <Typography>You are about to refund:</Typography>
          <Typography style={{ marginTop: "0.5em" }}>
            Transaction: <strong>{rTransactionId}</strong>
          </Typography>
          <Typography>User: <strong>{rEmail}</strong></Typography>
          <Typography style={{ marginTop: "0.5em" }}>
            Amount: <strong>£{rLookup?.transaction.amount}</strong>
          </Typography>
          {needsBankDetails ? (
            <>
              <Typography>
                Recipient: <strong>{rName}</strong>
              </Typography>
              <Typography>
                Sort Code: <strong>{rSortCode}</strong> — Account: <strong>{rAccountNumber}</strong>
              </Typography>
              {rLookup?.source_details_found ? (
                <Typography style={{ marginTop: "0.5em", color: "#2e7d32" }}>
                  ✓ Source details from TrueLayer
                </Typography>
              ) : (
                <Typography style={{ marginTop: "0.5em", color: "#e65100" }}>
                  ⚠ Source details entered manually
                </Typography>
              )}
            </>
          ) : (
            <Typography>
              To: <strong>the account that paid, via {providerLabel(rLookup?.refund_provider)}</strong>
            </Typography>
          )}
          <Typography style={{ marginTop: "0.75em", color: "#c62828" }}>
            This will initiate a real bank transfer. Please confirm.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRConfirmOpen(false)} color="default">Cancel</Button>
          <Button onClick={handleRefundSend} color="primary" variant="contained">Confirm & Send</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Confirm: Archive Refund ═══ */}
      <Dialog open={!!archiveDialog} onClose={() => setArchiveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Archive Refund #{archiveDialog?.refund_id}</DialogTitle>
        <DialogContent>
          <Typography>
            Amount: <strong>£{archiveDialog?.amount}</strong>
          </Typography>
          <Typography>
            Gift Card Payment ID: <strong>{archiveDialog?.gift_card_user_payment_id ?? "—"}</strong>
          </Typography>
          <TextField
            className={classes.formField}
            label="Reason"
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            fullWidth
            required
            placeholder="e.g. natwest_rejected_1114"
            margin="normal"
          />
          <Typography style={{ marginTop: "0.75em", color: "#c62828" }}>
            This moves the refund to gift_card_payment_refund_archive and deletes it from the live
            table, freeing the payment for a new refund. It does not reverse any money already sent.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog(null)} color="default" disabled={archiveLoading}>Cancel</Button>
          <Button onClick={handleArchiveRefund} color="secondary" variant="contained" disabled={archiveLoading}>
            {archiveLoading ? <CircularProgress size={20} /> : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Raw Request / Response Details ═══ */}
      <Dialog
        open={!!detailsDialog}
        onClose={() => setDetailsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{detailsDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography className={classes.jsonSectionTitle}>Request</Typography>
          <pre className={classes.jsonBlock}>
            {detailsDialog?.source ? JSON.stringify(detailsDialog.source, null, 2) : "N/A"}
          </pre>
          <Typography className={classes.jsonSectionTitle}>Response</Typography>
          <pre className={classes.jsonBlock}>
            {detailsDialog?.response ? JSON.stringify(detailsDialog.response, null, 2) : "N/A"}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(null)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default NatwestTransferDashboard;
